import {
  hasAllPermissionsInArray,
  hasAtLeastOnePermissionsInArray,
  hasPermissionInArray,
  isBelongToTeam,
} from '@userProfile/permission/permission.services';
import { useContext, useMemo } from 'react';
import { AppConfigContext } from './AppConfig';

export const useAppConfigs = () => {
  const appConfigs = useContext(AppConfigContext);
  return appConfigs;
};

export const useHasPermission = (permissionCode: string, companyTypeId?: number, organizationTypeId?: number) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    return hasPermissionInArray(appConfigs.configs.myPermissions, permissionCode, companyTypeId, organizationTypeId);
  }, [JSON.stringify(appConfigs.configs.myPermissions), permissionCode, companyTypeId, organizationTypeId]);
  return result;
};

export const useHasPermissions = (
  permissionCodes: string[],
  companyTypeId?: number,
  organizationTypeId?: number,
  shouldHasAllPermissions = true,
) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    if (!permissionCodes || permissionCodes.length <= 0) return true;
    if (shouldHasAllPermissions)
      return hasAllPermissionsInArray(
        appConfigs.configs.myPermissions,
        permissionCodes,
        companyTypeId,
        organizationTypeId,
      );
    return hasAtLeastOnePermissionsInArray(
      appConfigs.configs.myPermissions,
      permissionCodes,
      companyTypeId,
      organizationTypeId,
    );
  }, [
    JSON.stringify(appConfigs.configs.myPermissions),
    permissionCodes,
    shouldHasAllPermissions,
    companyTypeId,
    organizationTypeId,
  ]);

  return result;
};

export const useMyUserProfile = () => {
  const appConfigs = useAppConfigs();
  return appConfigs.configs.myUserProfile;
};

export const useProjectId = () => {
  const appConfigs = useAppConfigs();
  return appConfigs.configs.project?.id ?? 0;
};
export const useProjectIsArchived = () => {
  const appConfig = useAppConfigs();
  return appConfig.configs.project?.isArchived ?? false;
};

export const useIsFromCompany = (companyId: number) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    return appConfigs.configs.myCompanies?.some((c) => c.id === companyId) || false;
  }, [JSON.stringify(appConfigs.configs.myCompanies), companyId]);

  return result;
};

export const useHasOverlappingCompany = (companyIds: number[] | undefined) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    const found = appConfigs.configs.myCompanies?.find((c) => companyIds?.includes(c.id));
    return !!found;
  }, [JSON.stringify(appConfigs.configs.myCompanies), companyIds]);

  return result;
};

export const useIsFromCompanyByName = (companyName: string) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    return appConfigs.configs.myCompanies?.some((c) => c.name === companyName) || false;
  }, [JSON.stringify(appConfigs.configs.myCompanies), companyName]);

  return result;
};

export const useIsFromTeam = (teamId: number | number[]) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    return isBelongToTeam(appConfigs.configs.myTeams ?? [], teamId);
  }, [JSON.stringify(appConfigs.configs.myTeams), teamId]);
  return result;
};

export const useIsFromCompanyByOrganizationTypeCodeByCompanyTypeName = (
  organizationTypeCode: string,
  companyTypeName: string,
) => {
  const appConfigs = useContext(AppConfigContext);
  const result = useMemo(() => {
    return (
      appConfigs.configs.myCompanies?.some(
        (c) =>
          c.companyType?.name === companyTypeName && c.organizationType?.organizationTypeCode === organizationTypeCode,
      ) || false
    );
  }, [JSON.stringify(appConfigs.configs.myCompanies), companyTypeName, organizationTypeCode]);

  return result;
};
